'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import gsap from 'gsap';
import { Flip } from 'gsap/Flip';
import { FC, useEffect, useRef, useState } from 'react';
import CaseSlide from '@/components/CasesChunkedSlider/CaseSlide';
import Filters from '@/components/CaseStudiesPage/Filters';
import { useCaseStudiesFiltering } from '@/components/CaseStudiesPage/GridCaseStudies/hooks/useCaseStudiesFiltering';
import { useFiltersPanelAnimation } from '@/components/CaseStudiesPage/GridCaseStudies/hooks/useFiltersPanelAnimation';
import { useGridFilteringAnimation } from '@/components/CaseStudiesPage/GridCaseStudies/hooks/useGridFilteringAnimation';
import { useGridInitialAnimation } from '@/components/CaseStudiesPage/GridCaseStudies/hooks/useGridInitialAnimation';
import { useGridTitleAnimation } from '@/components/CaseStudiesPage/GridCaseStudies/hooks/useGridTitleAnimation';
import { mapCaseToSlideItem } from '@/components/CaseStudiesPage/mapCaseToSlideItem';
import MobileFiltersSheet from '@/components/CaseStudiesPage/MobileFiltersSheet';
import { PortfolioNotFoundDto } from '@/components/CaseStudiesPage/types';
import FilterEmptyResult from '@/components/shared/FilterEmptyResult';
import { Typography } from '@/components/shared/Typography';
import useDevice from '@/hooks/useDevice';
import { CaseStudyDto } from '@/types/case-studies';
import css from './style.module.scss';
gsap.registerPlugin(Flip);
interface Props {
  cases: CaseStudyDto[];
  allCasesTitle: string;
  notFound: PortfolioNotFoundDto;
}
const GridCaseStudies: FC<Props> = t0 => {
  const $ = _c(83);
  const {
    cases,
    allCasesTitle,
    notFound
  } = t0;
  const device = useDevice();
  const isMobile = device === "mobile";
  const wrapperRef = useRef(null);
  const flipStateRef = useRef(null);
  const containerHeightRef = useRef(0);
  const isTransitioningFromEmptyRef = useRef(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [showAllCases, setShowAllCases] = useState(false);
  const {
    services,
    industries,
    selectedServices,
    selectedIndustries,
    filteredCases,
    toggleFilter,
    clearFilters,
    resetAllFilters
  } = useCaseStudiesFiltering(cases);
  const activeFiltersCount = selectedServices.length + selectedIndustries.length;
  const hasNoResults = filteredCases.length === 0;
  let t1;
  if ($[0] !== filteredCases || $[1] !== isMobile || $[2] !== showAllCases) {
    t1 = isMobile && !showAllCases ? filteredCases.slice(0, 4) : filteredCases;
    $[0] = filteredCases;
    $[1] = isMobile;
    $[2] = showAllCases;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const displayedCases = t1;
  const hasMoreCases = isMobile && filteredCases.length > 4 && !showAllCases;
  let t2;
  if ($[4] !== toggleFilter) {
    t2 = (type, filter) => {
      if (wrapperRef.current) {
        flipStateRef.current = Flip.getState(".case-item");
        containerHeightRef.current = wrapperRef.current.offsetHeight;
      }
      toggleFilter(type, filter);
    };
    $[4] = toggleFilter;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleToggleFilter = t2;
  let t3;
  if ($[6] !== clearFilters) {
    t3 = category => {
      if (wrapperRef.current) {
        flipStateRef.current = Flip.getState(".case-item");
        containerHeightRef.current = wrapperRef.current.offsetHeight;
      }
      clearFilters(category);
    };
    $[6] = clearFilters;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleClearFilters = t3;
  let t4;
  if ($[8] !== hasNoResults || $[9] !== resetAllFilters) {
    t4 = async () => {
      if (wrapperRef.current && hasNoResults) {
        isTransitioningFromEmptyRef.current = true;
        containerHeightRef.current = wrapperRef.current.offsetHeight;
        const emptyResult = wrapperRef.current.querySelector(".empty-result");
        if (emptyResult) {
          await gsap.to(emptyResult, {
            opacity: 0,
            scale: 0.9,
            duration: 0.4,
            ease: "power1.in"
          });
        }
      }
      resetAllFilters();
    };
    $[8] = hasNoResults;
    $[9] = resetAllFilters;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleResetAllFilters = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsFiltersOpen(true);
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleOpenFilters = t5;
  let t6;
  if ($[12] !== isMobile) {
    t6 = () => {
      if (isMobile) {
        setIsFiltersOpen(false);
        return;
      }
      if (wrapperRef.current) {
        const filtersWrapper = wrapperRef.current.querySelector(`.${css.desktopFiltersWrapper}`);
        if (filtersWrapper) {
          gsap.to(filtersWrapper, {
            height: 0,
            opacity: 0,
            duration: 0.4,
            ease: "power2.in",
            onComplete: () => setIsFiltersOpen(false)
          });
          return;
        }
      }
      setIsFiltersOpen(false);
    };
    $[12] = isMobile;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleCloseFilters = t6;
  let t7;
  if ($[14] !== allCasesTitle) {
    t7 = {
      wrapperRef,
      titleSelector: `.${css.title}`,
      allCasesTitle
    };
    $[14] = allCasesTitle;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  useGridTitleAnimation(t7);
  let t8;
  if ($[16] !== displayedCases) {
    t8 = {
      wrapperRef,
      displayedItems: displayedCases,
      cssItemsSelector: ".case-item",
      flipStateRef,
      isTransitioningFromEmptyRef
    };
    $[16] = displayedCases;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  useGridInitialAnimation(t8);
  let t9;
  if ($[18] !== displayedCases || $[19] !== hasNoResults) {
    t9 = {
      wrapperRef,
      displayedCases,
      hasNoResults,
      flipStateRef,
      containerHeightRef,
      isTransitioningFromEmptyRef
    };
    $[18] = displayedCases;
    $[19] = hasNoResults;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  useGridFilteringAnimation(t9);
  let t10;
  if ($[21] !== isFiltersOpen || $[22] !== isMobile) {
    t10 = {
      wrapperRef,
      isFiltersOpen,
      isMobile,
      filtersWrapperSelector: `.${css.desktopFiltersWrapper}`
    };
    $[21] = isFiltersOpen;
    $[22] = isMobile;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  useFiltersPanelAnimation(t10);
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setShowAllCases(false);
    };
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== selectedServices) {
    t12 = [selectedServices];
    $[25] = selectedServices;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[27] !== isFiltersOpen || $[28] !== isMobile) {
    t13 = () => {
      if (!isMobile) {
        return;
      }
      const htmlElement = document.documentElement;
      if (isFiltersOpen) {
        htmlElement.style.overflow = "hidden";
      } else {
        htmlElement.style.overflow = "";
      }
      return () => {
        if (isMobile) {
          htmlElement.style.overflow = "";
        }
      };
    };
    t14 = [isFiltersOpen, isMobile];
    $[27] = isFiltersOpen;
    $[28] = isMobile;
    $[29] = t13;
    $[30] = t14;
  } else {
    t13 = $[29];
    t14 = $[30];
  }
  useEffect(t13, t14);
  const t15 = filteredCases?.length;
  let t16;
  if ($[31] !== t15) {
    t16 = <sup>{t15}</sup>;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== allCasesTitle || $[34] !== t16) {
    t17 = <Typography variant="h4" tag="h2" className={css.title}>{allCasesTitle} {t16}</Typography>;
    $[33] = allCasesTitle;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const t18 = isFiltersOpen && css.filtersButtonOpen;
  let t19;
  if ($[36] !== t18) {
    t19 = clsx(css.filtersButton, t18);
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== handleCloseFilters || $[39] !== isFiltersOpen) {
    t20 = () => isFiltersOpen ? handleCloseFilters() : handleOpenFilters();
    $[38] = handleCloseFilters;
    $[39] = isFiltersOpen;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== activeFiltersCount) {
    t21 = activeFiltersCount > 0 && <sup className={css.filtersCount}>{activeFiltersCount}</sup>;
    $[41] = activeFiltersCount;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t21) {
    t22 = <Typography variant="body-m" tag="span">Filters{" "}{t21}</Typography>;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" className={css.plusIcon}><path d="M0 5.99829H12" stroke="currentColor" /><path d="M6 0L6 12" stroke="currentColor" /></svg>;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== t19 || $[47] !== t20 || $[48] !== t22) {
    t24 = <button className={t19} onClick={t20}>{t22}{t23}</button>;
    $[46] = t19;
    $[47] = t20;
    $[48] = t22;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t17 || $[51] !== t24) {
    t25 = <div className={css.head}>{t17}{t24}</div>;
    $[50] = t17;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== handleClearFilters || $[54] !== handleToggleFilter || $[55] !== industries || $[56] !== isFiltersOpen || $[57] !== isMobile || $[58] !== selectedIndustries || $[59] !== selectedServices || $[60] !== services) {
    t26 = isFiltersOpen && !isMobile ? <div className={css.desktopFiltersWrapper}><Filters services={services} industries={industries} selectedServices={selectedServices} selectedIndustries={selectedIndustries} onToggleFilter={handleToggleFilter} onClearFilters={handleClearFilters} /></div> : null;
    $[53] = handleClearFilters;
    $[54] = handleToggleFilter;
    $[55] = industries;
    $[56] = isFiltersOpen;
    $[57] = isMobile;
    $[58] = selectedIndustries;
    $[59] = selectedServices;
    $[60] = services;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== handleClearFilters || $[63] !== handleCloseFilters || $[64] !== handleToggleFilter || $[65] !== industries || $[66] !== isFiltersOpen || $[67] !== isMobile || $[68] !== selectedIndustries || $[69] !== selectedServices || $[70] !== services) {
    t27 = isMobile && isFiltersOpen ? <MobileFiltersSheet services={services} industries={industries} selectedServices={selectedServices} selectedIndustries={selectedIndustries} onToggleFilter={handleToggleFilter} onClearFilters={handleClearFilters} onClose={handleCloseFilters} /> : null;
    $[62] = handleClearFilters;
    $[63] = handleCloseFilters;
    $[64] = handleToggleFilter;
    $[65] = industries;
    $[66] = isFiltersOpen;
    $[67] = isMobile;
    $[68] = selectedIndustries;
    $[69] = selectedServices;
    $[70] = services;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] !== displayedCases || $[73] !== handleResetAllFilters || $[74] !== hasMoreCases || $[75] !== hasNoResults || $[76] !== notFound) {
    t28 = hasNoResults ? <FilterEmptyResult className={clsx(css.emptyResult, "empty-result")} title={notFound.title} text={notFound.description} backLabel={notFound.btnText} onClearFilters={handleResetAllFilters} /> : <><ul className={css.grid}>{displayedCases.map(_temp)}</ul>{hasMoreCases ? <div className={css.showMoreButtonWrapper}><button className={css.showMoreButton} onClick={() => setShowAllCases(true)}>Show more cases</button></div> : null}</>;
    $[72] = displayedCases;
    $[73] = handleResetAllFilters;
    $[74] = hasMoreCases;
    $[75] = hasNoResults;
    $[76] = notFound;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== t25 || $[79] !== t26 || $[80] !== t27 || $[81] !== t28) {
    t29 = <div ref={wrapperRef} className={css.wrapper}>{t25}{t26}{t27}{t28}</div>;
    $[78] = t25;
    $[79] = t26;
    $[80] = t27;
    $[81] = t28;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  return t29;
};
export default GridCaseStudies;
function _temp(caseItem) {
  const [slideItem] = mapCaseToSlideItem(caseItem);
  if (!slideItem) {
    return null;
  }
  return <div key={caseItem.id} className="case-item" data-flip-id={caseItem.id}><CaseSlide data={slideItem} width={100} withOverlay={false} hideDefaultCursor={false} /></div>;
}